import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AuthGate } from './components/guards/AuthGate';
import { ClientScopeGate } from './components/guards/ClientScopeGate';
import { AppShell } from './components/shell/AppShell';
import { useMeContext } from './context/MeContextProvider';
import { LoginPage } from './pages/LoginPage';
import { AcceptInvitationPage } from './pages/AcceptInvitationPage';
// Self-serve agency signup (Task agency-signup-v1). Public route that
// creates a brand-new agency organization + owner profile + owner
// `user_role` via the `signup-agency` Edge Function. Distinct from
// `/accept-invitation`, which redeems an existing token-hash row in
// `user_invitations` for users joining an existing agency.
import { AgencySignupPage } from './pages/AgencySignupPage';
import { AgencyOnboardingPage } from './pages/AgencyOnboardingPage';
import { SupportPage } from './pages/SupportPage';
// Task 112C — social-connection OAuth callback landing page.
// Provider redirects land here with ?state=&code=. The page calls
// `complete-social-connection-oauth` and navigates back.
import { SocialOAuthCallbackPage } from './pages/auth/SocialOAuthCallbackPage';
// Task 141 (IC-3) — paid-media OAuth callback landing page on the
// integration-credential layer. Separate from the publishing
// callback per IC-1 §2 — different EFs, different wire markers,
// different success-body shape, different picker UX.
import { IntegrationOAuthCallbackPage } from './pages/auth/IntegrationOAuthCallbackPage';
// Auth UX — forgot/reset password flow. Both pages are unauthenticated.
// `/forgot-password` sends the recovery email via
// `supabase.auth.resetPasswordForEmail`; `/reset-password` consumes the
// PKCE `?code=...` via `exchangeCodeForSession` and then calls
// `supabase.auth.updateUser({ password })` to set the new password.
// `/reset-password` MUST be added to the Supabase Dashboard's
// Authentication → URL Configuration → Redirect URLs whitelist; see
// `apps/web/.env.example` for the operator note.
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { AppLanding } from './pages/AppLanding';
import { ContentWriterWorkspaceRoute } from './pages/ContentWriterWorkspace';
import { WritingReviewRoute } from './pages/SeniorContentWriterWorkspace';
import { ClientsListPage } from './pages/ClientsListPage';
// Task 118 — Employee Workspace v1. Focused "مهامي" landing for
// agency production-execute staff (designer / video_editor /
// content_writer / etc.). AppLanding redirects them here.
import { EmployeeTasksPage } from './pages/EmployeeTasksPage';
import { ClientWorkspaceLayout } from './pages/ClientWorkspaceLayout';
import { OnboardingWizardPage } from './pages/client/OnboardingWizardPage';
import { OverviewPage } from './pages/client/OverviewPage';
import { ClientTeamPage } from './pages/client/ClientTeamPage';
import { StrategyPage } from './pages/client/StrategyPage';
import { ContentPage } from './pages/client/ContentPage';
import { PublishingPage } from './pages/client/PublishingPage';
import { PaidMediaPage } from './pages/client/PaidMediaPage';
import { CreativePage } from './pages/client/CreativePage';
import { CreativeIntelligencePage } from './pages/client/CreativeIntelligencePage';
import { InfluencersPage } from './pages/client/InfluencersPage';
import { ReportsPage } from './pages/client/ReportsPage';
import { AgencyInfluencersPage } from './pages/AgencyInfluencersPage';
import { AgencyCrmPage } from './pages/AgencyCrmPage';
import { AgencyReportsPage } from './pages/AgencyReportsPage';
import { AgencyCreativePage } from './pages/AgencyCreativePage';
import { AgencyPaidMediaPage } from './pages/AgencyPaidMediaPage';
import { AgencyKnowledgeLibraryPage } from './pages/AgencyKnowledgeLibraryPage';
import { AgencyServicesPage } from './pages/AgencyServicesPage';
import { ClientServicesPage } from './pages/client/ClientServicesPage';
import { ClientContractPage } from './pages/client/ClientContractPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { SettingsPage } from './pages/SettingsPage';
import { TeamStubPage } from './pages/TeamStubPage';
// ── Task 298 (Foundation F) — commercial standing ────────────────────
// One route block, appended in merge order per the shared-file protocol
// in `tasks/295A-commercial-foundation-and-staging.md` §9.3.
//
// `CommercialStandingGate` is F's MINIMAL `/app` guard: a non-operational
// tenant is routed to `ActivationPendingPage`, resolved from the
// activation-status boundary and NOT from any permission key. A fallback
// screen that nothing routes to is not a fallback, and F is the PR that
// turns the gate on.
//
// AFTER F, LANE O (Task 299) IS THE ONLY LANE THAT MAY TOUCH THE GUARD,
// and it replaces this minimal redirect with the full pending /
// suspended / terminated journey. Sequential ownership transfer across a
// merge boundary — never two concurrent owners. Lanes B and C add routes
// INSIDE the existing shell and must not touch the guard.
import { CommercialStandingGate } from './components/guards/CommercialStandingGate';
import { PlatformCommercialPage } from './pages/platform/PlatformCommercialPage';
import { PlatformAgencyDetailPage } from './pages/platform/PlatformAgencyDetailPage';
// ── Task 301 (Lane B) — agency SaaS subscription / billing presentation ──
import { AgencyBillingPage } from './pages/AgencyBillingPage';
import { LoadingState, NotFoundState, PermissionDeniedState } from './components/states';
import { hasAnyPermission, hasRoleCategory, PERM } from './permissions';

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/app" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
      <Route path="/support" element={<SupportPage />} />
      {/* Task agency-signup-v1 — public self-serve agency signup. */}
      <Route path="/signup/agency" element={<AgencySignupPage />} />
      {/* Task 112C — social OAuth provider redirect lands here. The
          page is auth-gated by AuthGate (the user MUST be signed in
          when the provider redirects back), so it sits inside the
          /app subtree as a sibling of clients/. */}
      <Route
        path="/auth/social-oauth-callback"
        element={
          <AuthGate>
            <SocialOAuthCallbackPage />
          </AuthGate>
        }
      />
      {/* Task 141 (IC-3) — paid-media OAuth callback. Auth-gated for
          the same reason as the publishing callback: the provider
          redirects back into the signed-in session, and the callback
          posts to the IC-3 EF under the user's bearer. Separate path
          from /auth/social-oauth-callback per IC-1 §2. */}
      <Route
        path="/auth/integration-oauth-callback"
        element={
          <AuthGate>
            <IntegrationOAuthCallbackPage />
          </AuthGate>
        }
      />
      <Route
        path="/app"
        element={
          <AuthGate>
            {/* Task 298 — the standing gate sits INSIDE AuthGate (it needs
                a session to resolve its own organization) and ABOVE
                AppShell (so no product surface renders for a
                non-operational tenant). It holds NO OPINION when the
                activation-status endpoint is unreachable — see the
                transport rule in CommercialStandingGate. */}
            <CommercialStandingGate>
              <AppShell />
            </CommercialStandingGate>
          </AuthGate>
        }
      >
        <Route index element={<AppLanding />} />
        {/* Task 118 — Employee Workspace v1. No extra permission gate
            here: the page calls `list-my-production-tasks` which is
            RLS-bound (filters to `assignee_user_id = auth.uid()`).
            Sysadmins and users with no active assignments just see an
            empty list — no data leakage. AppLanding does the
            role-aware redirect that lands production-only staff here
            by default. */}
        <Route path="me/tasks" element={<EmployeeTasksPage />} />
        {/* Task 292C1-QA-A — the canonical writer deep link. The writer
            workspace previously had NO route (AppLanding rendered it inline at
            `/app`), so «افتح المهمة» could not point at it and a specific task
            could not be addressed, refreshed, or reached by back/forward.
            `:taskId` is the STABLE task id; the workspace selects by it and
            renders an explicit not-found panel when it does not resolve
            (the queue is RLS-bound, so "not in the list" means not yours).

            AUTHORIZATION: unlike `me/tasks` above — a GENERIC queue — this is a
            ROLE-SPECIFIC workspace, so both routes are gated by
            `canOpenWriterWorkspace` inside ContentWriterWorkspaceRoute (active
            agency `content_writer` + `task.writing.execute`, sysadmins
            excluded), per SECURITY.md line 29. Do NOT remove that gate: without
            it a direct URL bypasses AppLanding's role precedence. */}
        <Route path="me/writing" element={<ContentWriterWorkspaceRoute />} />
        <Route path="me/writing/:taskId" element={<ContentWriterWorkspaceRoute />} />
        {/* Task 292C2 — the WRITING REVIEW lane. Reachable by
            senior_content_writer AND by owner / marketing_manager as
            operational fallback reviewers: before this task the review surface
            existed only as the AppLanding default for senior_content_writer,
            behind `!isOwner && !isMarketingManager && ...`, so the two fallback
            reviewers were guarded OUT of the only surface that could reach the
            RPC they are permitted to call. `:draftId` keeps the selection
            stable across refresh and back/forward. Gated by
            `canOpenWritingReview` inside WritingReviewRoute (reviewer role +
            `content.writing.approve`, sysadmins excluded), per SECURITY.md
            line 29 — do NOT remove that gate. */}
        <Route path="writing-review" element={<WritingReviewRoute />} />
        <Route path="writing-review/:draftId" element={<WritingReviewRoute />} />
        <Route path="clients" element={<ClientsListPage />} />
        <Route
          path="clients/:clientId"
          element={
            <ClientScopeGate>
              <ClientWorkspaceLayout />
            </ClientScopeGate>
          }
        >
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<OverviewPage />} />
          <Route path="onboarding" element={<OnboardingWizardPage />} />
          {/* Task 109 — client team management. Leadership-only; the
              page self-guards (PermissionDeniedState) on a direct URL
              hit and the workspace layout hides the tab otherwise. */}
          <Route path="team" element={<ClientTeamPage />} />
          <Route path="strategy" element={<StrategyPage />} />
          <Route path="content" element={<ContentPage />} />
          <Route path="publishing" element={<PublishingPage />} />
          <Route path="paid-media" element={<PaidMediaPage />} />
          <Route path="creative" element={<CreativePage />} />
          {/* Task 223 — Creative Intelligence is a CLIENT-scoped surface (was
              the agency `/app/creative-intelligence`). Tab visibility is gated
              by CREATIVE_INTELLIGENCE_READ in ClientWorkspaceLayout; the page's
              EFs + RLS are the load-bearing security gate. */}
          <Route path="creative-intelligence" element={<CreativeIntelligencePage />} />
          <Route path="influencers" element={<InfluencersPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="services" element={<ClientServicesPage />} />
          {/* Task 303-A — the agency-client contract (externally executed
              PDF). Inside ClientScopeGate like every other client tab. No
              RequirePermission wrapper: the page renders from ONE server
              answer that already decided which surface (agency or
              client-safe) the caller is entitled to, and every endpoint
              re-applies its own gates. A direct URL hit by an
              unpermitted actor gets the endpoint's 403/404, which the
              page renders as an explicit state. */}
          <Route path="contract" element={<ClientContractPage />} />
        </Route>
        <Route
          path="influencers"
          element={
            <RequirePermission anyOf={[PERM.INFLUENCER_CATALOG_READ]}>
              <AgencyInfluencersPage />
            </RequirePermission>
          }
        />
        <Route
          path="crm"
          element={
            <RequirePermission anyOf={[PERM.CRM_LEAD_READ, PERM.CRM_DEAL_READ]}>
              <AgencyCrmPage />
            </RequirePermission>
          }
        />
        <Route
          path="reports"
          element={
            <RequirePermission anyOf={[PERM.REPORT_READ, PERM.REPORT_FINANCE_READ]}>
              <AgencyReportsPage />
            </RequirePermission>
          }
        />
        <Route
          path="creative"
          element={
            <RequirePermission anyOf={[PERM.CREATIVE_INTELLIGENCE_READ]}>
              <AgencyCreativePage />
            </RequirePermission>
          }
        />
        {/* Task 223 — the agency-level `/app/creative-intelligence` route was
            removed; Creative Intelligence now lives inside the client workspace
            at `/app/clients/:clientId/creative-intelligence`. */}
        <Route
          path="paid-media"
          element={
            <RequirePermission
              anyOf={[PERM.PAID_MEDIA_ACCOUNT_READ, PERM.PAID_MEDIA_CAMPAIGN_READ]}
            >
              <AgencyPaidMediaPage />
            </RequirePermission>
          }
        />
        <Route
          path="services"
          element={
            <RequirePermission anyOf={[PERM.BILLING_READ, PERM.CONTRACT_READ]} agencyOnly>
              <AgencyServicesPage />
            </RequirePermission>
          }
        />
        <Route
          path="knowledge-library"
          element={
            <RequirePermission anyOf={[PERM.AI_KNOWLEDGE_PACK_MANAGE]} agencyOnly>
              <AgencyKnowledgeLibraryPage />
            </RequirePermission>
          }
        />
        {/* Task 298 — the operator control plane. PLATFORM-ONLY: the page
            gates on `me.system_admin`, the same direct platform-role fact
            the server enforces as the first statement of both definer
            RPCs. It is deliberately NOT wrapped in RequirePermission —
            there is no `platform.*` permission key and creating one would
            produce the appearance of a control rather than a control
            (295A §6.2). RequirePermission would in fact REFUSE a sysadmin,
            since it denies `me.system_admin` outright. */}
        <Route path="platform/commercial" element={<PlatformCommercialPage />} />
        {/* Task 307A — the operator's agency DETAIL, as a real route.

            306A expanded this inside the list row, so it could not be
            linked to, did not survive a refresh, and left no history
            entry naming the agency being reviewed. It is a route now, and
            the id in the URL is what makes refresh and Back/Forward land
            on the same tenant.

            SAME GATE AS THE LIST, and for the same reason: `RequirePermission`
            would in fact REFUSE a sysadmin, since it denies
            `me.system_admin` outright, and there is no `platform.*`
            permission key to hang it on (295A §6.2). The page gates on
            `me.system_admin`; the two endpoints behind it check
            `public.system_admins` themselves. The UI is not the control. */}
        <Route path="platform/commercial/:organizationId" element={<PlatformAgencyDetailPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="onboarding" element={<AgencyOnboardingPage />} />
        <Route path="support" element={<SupportPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="team" element={<TeamStubPage />} />
        {/* Task 303-A-B — client contract continuity.

            The ONE surface a client keeps when its agency is suspended or
            terminated. It renders the SAME page as the client tab, with no
            `:clientId`: the endpoint answers for every client the caller is
            live-assigned to, so no client id crosses the wire in either
            direction (303-A §2.10).

            IT SITS HERE, NOT UNDER `clients/:clientId`, ON PURPOSE.
            `ClientScopeGate` admits a non-leadership actor only when the
            URL's client id appears in `me.client_assignments`, and
            `me/context` returns that array EMPTY for a client of a
            non-operational tenant — `client_assignments_select` is
            `organization_id = app.current_org_id()`, which is
            `app.org_is_operational()`-gated. Widening that gate would open
            every client tab; adding a sibling route opens exactly one page.
            `ClientScopeGate` is therefore not modified by this task.

            No `RequirePermission` wrapper, for the same reason the client
            tab has none: the page renders from one server answer, and the
            RPC behind it refuses every actor the decision does not name —
            agency and staff actors of the suspended tenant included. */}
        <Route path="contracts" element={<ClientContractPage />} />
        {/* Task 301 (Lane B) — the agency's own subscription to Super Agency.
            One route block, appended in merge order per the shared-file
            protocol in `tasks/295A-commercial-foundation-and-staging.md` §9.3.

            INSIDE the existing shell. Lane B does not touch
            `CommercialStandingGate` — Lane O owns it (§9.3) — and adds no
            standing check of its own here: the endpoint refuses a
            non-operational tenant server-side with
            `commercial_activation_required`.

            `agencyOnly` mirrors `/app/services`: this is agency-internal
            commercial data, and the endpoint repeats the same agency-category
            probe server-side. Route gating is UX; the 403 is the control. */}
        <Route
          path="billing"
          element={
            <RequirePermission anyOf={[PERM.BILLING_READ]} agencyOnly>
              <AgencyBillingPage />
            </RequirePermission>
          }
        />
      </Route>
      <Route path="*" element={<NotFoundState homePath="/app" />} />
    </Routes>
  );
}

function RequirePermission({
  anyOf,
  agencyOnly = false,
  children,
}: {
  readonly anyOf: ReadonlyArray<string>;
  readonly agencyOnly?: boolean;
  readonly children: ReactNode;
}) {
  const { me } = useMeContext();

  if (!me) {
    return <LoadingState title="جاري تحميل الصلاحيات" />;
  }

  if (
    me.system_admin ||
    !hasAnyPermission(me.permissions, ...anyOf) ||
    (agencyOnly && !hasRoleCategory(me, 'agency'))
  ) {
    return <PermissionDeniedState />;
  }

  return children;
}
